import React from 'react';

/**
 * PipelineStackBadge
 *
 * Tiny pill that tells admins which provider stack handled an analysis.
 * Drives the Cost-to-Serve dashboard's at-a-glance segmentation between
 * the legacy (AssemblyAI + OpenAI batch) and the new B4 (Groq + Modal
 * pyannote + Gemini Flash-Lite) stacks. Renders nothing for missing /
 * unknown values so it can be dropped anywhere without a guard.
 *
 * Color language follows our existing risk-first convention used by
 * CostToServePanel's token-delta pills: green = improvement / desired,
 * gray = neutral / legacy, amber = caution / mixed.
 */

interface PipelineStackBadgeProps {
  stack: string | null | undefined;
  className?: string;
  /** Show "Groq + Modal + Gemini"-style provider list next to the label. */
  showDetail?: boolean;
}

const STACK_CONFIG: Record<
  string,
  { label: string; detail: string; classes: string }
> = {
  b4: {
    label: 'B4',
    detail: 'Groq + Modal + Gemini',
    classes: 'bg-green-100 text-green-800 ring-green-200',
  },
  legacy: {
    label: 'Legacy',
    detail: 'AssemblyAI + OpenAI',
    classes: 'bg-gray-100 text-gray-700 ring-gray-300',
  },
  mixed: {
    label: 'Mixed',
    detail: 'Both stacks present',
    classes: 'bg-amber-100 text-amber-800 ring-amber-200',
  },
  unknown: {
    label: 'Unknown',
    detail: '',
    classes: 'bg-gray-100 text-gray-500 ring-gray-200',
  },
};

const PipelineStackBadge: React.FC<PipelineStackBadgeProps> = ({
  stack,
  className = '',
  showDetail = true,
}) => {
  if (!stack) return null;
  const config = STACK_CONFIG[stack.toLowerCase()] ?? STACK_CONFIG.unknown;
  const tooltip = config.detail
    ? `${config.label} stack — ${config.detail}`
    : `${config.label} stack`;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${config.classes} ${className}`}
      title={tooltip}
    >
      <span className="font-semibold uppercase tracking-wide">
        {config.label}
      </span>
      {showDetail && config.detail && (
        <span className="font-normal opacity-80">· {config.detail}</span>
      )}
    </span>
  );
};

export default PipelineStackBadge;
